import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, UserPlus } from "lucide-react";
import { BRAND } from "@/lib/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CEDULA_OPTIONS = [
  "Sí — Cédula A",
  "Sí — Cédula A1",
  "Sí — Cédula B",
  "No, quiero certificarme",
];
const EXPERIENCE_OPTIONS = [
  "Más de 3 años",
  "Entre 1 y 3 años",
  "Menos de 1 año",
  "Sin experiencia previa",
];

const JoinTeam = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    city: "",
    cedula_status: CEDULA_OPTIONS[0],
    experience: EXPERIENCE_OPTIONS[0],
    motivation: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const setField = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.city) {
      toast.error("Completa nombre, email, teléfono y ciudad.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/applications`, form);
      toast.success(
        "Postulación recibida. Te contactaremos para coordinar una entrevista.",
      );
      setForm({
        name: "",
        email: "",
        phone: "",
        city: "",
        cedula_status: CEDULA_OPTIONS[0],
        experience: EXPERIENCE_OPTIONS[0],
        motivation: "",
      });
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        "No se pudo enviar la postulación. Intenta de nuevo.";
      toast.error(typeof detail === "string" ? detail : "Error al enviar");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="unete"
      data-testid="join-team-section"
      className="relative bg-[#F9F6F0] text-[#0B0410] py-24 lg:py-32 border-t border-[#0B0410]/5 overflow-hidden"
    >
      <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-[#3D1565]/8 blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] rounded-full bg-[#E6D5B8]/30 blur-[120px] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 lg:gap-16">
        {/* LEFT — pitch */}
        <div className="lg:col-span-5">
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-10 bg-[#3D1565]" />
            <span
              className="text-[11px] tracking-luxury uppercase text-[#3D1565] font-semibold"
              data-testid="join-eyebrow"
            >
              Únete al equipo
            </span>
          </div>
          <h2
            data-testid="join-headline"
            className="font-display text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.05] text-[#0B0410]"
          >
            Construye tu carrera con{" "}
            <span className="italic text-[#3D1565]">respaldo institucional</span>.
          </h2>
          <p className="mt-6 text-base lg:text-lg text-[#0B0410]/70 font-light max-w-md">
            Buscamos asesores con visión, ética y hambre de crecer. Si quieres
            construir una carrera en finanzas y protección con respaldo de
            Allianz, Skandia y Dominion, déjanos tus datos y agendamos una
            entrevista.
          </p>

          <div className="mt-10 space-y-5">
            {[
              {
                t: "Acceso institucional",
                d: "Productos de Allianz México, Skandia, Dominion y las principales aseguradoras del país.",
              },
              {
                t: "Capacitación continua",
                d: "Te acompañamos hasta tu Cédula CNSF si aún no la tienes — y al MDRT si ya estás listo.",
              },
              {
                t: "Equipo, no aislamiento",
                d: "Once asesores trabajando juntos. Mentorías, casos reales y operación compartida.",
              },
            ].map((b) => (
              <div
                key={b.t}
                data-testid={`join-benefit-${b.t.toLowerCase().replace(/\s+/g, "-")}`}
                className="flex gap-4 items-start"
              >
                <div className="mt-1 w-8 h-8 flex items-center justify-center bg-[#3D1565] rounded-sm shrink-0">
                  <UserPlus size={14} className="text-[#E6D5B8]" />
                </div>
                <div>
                  <div className="font-display text-lg text-[#0B0410] leading-tight">
                    {b.t}
                  </div>
                  <p className="text-sm text-[#0B0410]/65 mt-1 font-light">
                    {b.d}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* RIGHT — form */}
        <div className="lg:col-span-7">
          <form
            onSubmit={onSubmit}
            data-testid="join-team-form"
            className="bg-white border border-[#0B0410]/10 rounded-sm p-8 lg:p-10 space-y-6 shadow-[0_30px_80px_-40px_rgba(61,21,101,0.2)]"
          >
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <label className="text-[11px] tracking-luxury uppercase text-[#0B0410]/55 mb-2 block">
                  Nombre completo
                </label>
                <input
                  type="text"
                  required
                  value={form.name}
                  onChange={setField("name")}
                  data-testid="join-input-name"
                  className="w-full bg-[#F9F6F0] border border-[#0B0410]/10 rounded-sm px-4 py-3 text-[#0B0410] focus:outline-none focus:border-[#3D1565] transition-colors"
                  placeholder="Tu nombre"
                />
              </div>
              <div>
                <label className="text-[11px] tracking-luxury uppercase text-[#0B0410]/55 mb-2 block">
                  Email
                </label>
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={setField("email")}
                  data-testid="join-input-email"
                  className="w-full bg-[#F9F6F0] border border-[#0B0410]/10 rounded-sm px-4 py-3 text-[#0B0410] focus:outline-none focus:border-[#3D1565] transition-colors"
                  placeholder="tu@correo.com"
                />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <label className="text-[11px] tracking-luxury uppercase text-[#0B0410]/55 mb-2 block">
                  Teléfono
                </label>
                <input
                  type="tel"
                  required
                  value={form.phone}
                  onChange={setField("phone")}
                  data-testid="join-input-phone"
                  className="w-full bg-[#F9F6F0] border border-[#0B0410]/10 rounded-sm px-4 py-3 text-[#0B0410] focus:outline-none focus:border-[#3D1565] transition-colors"
                  placeholder="+52 ..."
                />
              </div>
              <div>
                <label className="text-[11px] tracking-luxury uppercase text-[#0B0410]/55 mb-2 block">
                  Ciudad
                </label>
                <input
                  type="text"
                  required
                  value={form.city}
                  onChange={setField("city")}
                  data-testid="join-input-city"
                  className="w-full bg-[#F9F6F0] border border-[#0B0410]/10 rounded-sm px-4 py-3 text-[#0B0410] focus:outline-none focus:border-[#3D1565] transition-colors"
                  placeholder="Ciudad donde resides"
                />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <label className="text-[11px] tracking-luxury uppercase text-[#0B0410]/55 mb-2 block">
                  ¿Tienes Cédula CNSF?
                </label>
                <select
                  value={form.cedula_status}
                  onChange={setField("cedula_status")}
                  data-testid="join-select-cedula"
                  className="w-full bg-[#F9F6F0] border border-[#0B0410]/10 rounded-sm px-4 py-3 text-[#0B0410] focus:outline-none focus:border-[#3D1565] transition-colors appearance-none"
                >
                  {CEDULA_OPTIONS.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[11px] tracking-luxury uppercase text-[#0B0410]/55 mb-2 block">
                  Experiencia en finanzas / seguros
                </label>
                <select
                  value={form.experience}
                  onChange={setField("experience")}
                  data-testid="join-select-experience"
                  className="w-full bg-[#F9F6F0] border border-[#0B0410]/10 rounded-sm px-4 py-3 text-[#0B0410] focus:outline-none focus:border-[#3D1565] transition-colors appearance-none"
                >
                  {EXPERIENCE_OPTIONS.map((e) => (
                    <option key={e} value={e}>
                      {e}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="text-[11px] tracking-luxury uppercase text-[#0B0410]/55 mb-2 block">
                ¿Por qué quieres unirte a EMB? (opcional)
              </label>
              <textarea
                rows={4}
                value={form.motivation}
                onChange={setField("motivation")}
                data-testid="join-input-motivation"
                className="w-full bg-[#F9F6F0] border border-[#0B0410]/10 rounded-sm px-4 py-3 text-[#0B0410] focus:outline-none focus:border-[#3D1565] transition-colors resize-none"
                placeholder="Cuéntanos brevemente tus motivos y objetivos profesionales."
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              data-testid="join-submit"
              className="group w-full sm:w-auto bg-[#3D1565] hover:bg-[#4a1c79] disabled:opacity-60 disabled:cursor-not-allowed text-white px-8 py-4 rounded-sm transition-all duration-300 hover:-translate-y-0.5 flex items-center justify-center gap-2"
            >
              <span className="text-sm tracking-wider uppercase font-medium">
                {submitting ? "Enviando..." : "Solicitar entrevista"}
              </span>
              <ArrowRight
                size={16}
                className="transition-transform duration-300 group-hover:translate-x-1"
              />
            </button>

            <p className="text-xs text-[#0B0410]/55 leading-relaxed">
              Al enviar este formulario aceptas que EMB Consulting Group te
              contacte para fines de reclutamiento. Tus datos no se comparten
              con terceros.
            </p>
          </form>
        </div>
      </div>
    </section>
  );
};

export default JoinTeam;
